/**
 * Auth State Store
 * Centralizes user identity, authentication status, and guest state so any
 * component can read auth state without drilling through AuthContext props.
 * Backed by Zustand for reactive updates across the component tree.
 */
import { create } from 'zustand';

export interface User {
  id: string;
  email: string;
  is_active: boolean;
  is_superuser: boolean;
  is_admin?: boolean;
  is_verified: boolean;
  is_pro: boolean;
  fuel_credits: number;
  tier: string;
  storage_used_bytes: number;
  image_used_bytes: number;
  username?: string;
  display_name?: string;
  [key: string]: unknown;
}

interface AuthStore {
  // State
  user: User | null;
  /** Invalidate owner-bound work even when an account switches A -> B -> A. */
  identityEpoch: number;
  isAuthenticated: boolean;
  isGuestUser: boolean;
  // authToken: intentionally omitted — real auth uses an HttpOnly cookie (msaplingauth)
  // managed by the backend. Never store the JWT in JS-accessible memory.

  // Actions
  setUser: (user: User | null) => void;
  setAuthenticated: (val: boolean) => void;
  setGuestUser: (val: boolean) => void;
  /**
   * Patch ONLY the canonical fuel balance on the current user without
   * replacing the whole user object. Used by the live CAP_STATUS_UPDATE SSE
   * stream so the singular `user.fuel_credits` source stays in sync with the
   * server-pushed `credits_remaining` value. No-op when there is no user.
   */
  setFuelCredits: (credits: number) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthStore>((set) => ({
  user: null,
  identityEpoch: 0,
  isAuthenticated: false,
  isGuestUser: false,

  setUser: (user) => set(state => ({ user,
    identityEpoch: state.identityEpoch + (state.user?.id !== user?.id ? 1 : 0),
  })),
  setAuthenticated: (isAuthenticated) => set({ isAuthenticated }),
  setGuestUser: (isGuestUser) => set({ isGuestUser }),
  setFuelCredits: (credits) =>
    set((state) => {
      if (!state.user) return state;
      const next = Number(credits);
      if (!Number.isFinite(next) || next === state.user.fuel_credits) return state;
      return { user: { ...state.user, fuel_credits: next } };
    }),
  logout: () =>
    set(state => ({
      user: null,
      isAuthenticated: false,
      isGuestUser: false,
      identityEpoch: state.identityEpoch + 1,
    })),
}));
